const STORAGE_KEY = 'reservation-selection-v1';

type ReservationRoom = {
  adult: number;
  child: number;
  baby: number;
  room_type?: string | null;
  options?: Record<string, unknown>;
};

type ReservationSelection = {
  rooms: ReservationRoom[];
  childrenAges?: Array<number | string>;
  childPriceSelections?: Array<{
    age?: number | string;
    selectedPriceType?: string | null;
    selectedPriceId?: string | null;
  }>;
};

type TourLike = {
  turs_single?: string | number;
  turs_double?: string | number;
  turs_triple?: string | number;
  turs_child?: string | number;
  turs_baby?: string | number;
  prices?: Array<{
    turf_PaxType?: string;
    turf_RoomType?: string;
    turf_MinAge?: string | number;
    turf_MaxAge?: string | number;
    turf_Price?: string | number;
  }>;
};

const toNumber = (val: unknown) => {
  if (val === null || val === undefined) return 0;
  const normalized = String(val).replace(',', '.');
  const num = Number(normalized);
  return Number.isFinite(num) ? num : 0;
};

const getRoomKidsCount = (reservation: ReservationSelection) => {
  if (!reservation?.rooms) return 0;
  return reservation.rooms.reduce((sum, room) => {
    return sum + toNumber(room.child) + toNumber(room.baby);
  }, 0);
};

const getChildrenAgeCounts = (childrenAges: Array<number | string> = []) => {
  let baby = 0;
  let child = 0;
  let adultExtra = 0;
  let unknown = 0;

  childrenAges.forEach((age) => {
    if (age === '' || age === null || age === undefined) {
      unknown += 1;
      return;
    }
    const parsed = parseInt(String(age), 10);
    if (Number.isNaN(parsed)) {
      unknown += 1;
      return;
    }
    if (parsed <= 2) {
      baby += 1;
    } else if (parsed <= 11) {
      child += 1;
    } else {
      adultExtra += 1;
    }
  });

  return { baby, child, adultExtra, unknown };
};

const normalizeChildrenAges = (reservation: ReservationSelection) => {
  if (!reservation) return;
  if (!Array.isArray(reservation.childrenAges)) reservation.childrenAges = [];
  const totalKids = getRoomKidsCount(reservation);
  if (reservation.childrenAges.length > totalKids) {
    reservation.childrenAges = reservation.childrenAges.slice(0, totalKids);
    return;
  }
  while (reservation.childrenAges.length < totalKids) {
    reservation.childrenAges.push('');
  }
};

const removeChildFromRooms = (reservation: ReservationSelection, age: number | string | null | undefined) => {
  if (!reservation?.rooms || reservation.rooms.length === 0) return;

  const parsed = age === '' || age === null || age === undefined ? NaN : parseInt(String(age), 10);
  const preferBaby = !Number.isNaN(parsed) && parsed <= 2;
  const preferChild = !Number.isNaN(parsed) && parsed > 2;

  const tryDecrement = (field: 'child' | 'baby') => {
    for (let i = reservation.rooms.length - 1; i >= 0; i -= 1) {
      const room = reservation.rooms[i];
      const current = toNumber(room[field]);
      if (current > 0) {
        room[field] = current - 1;
        return true;
      }
    }
    return false;
  };

  if (preferBaby && tryDecrement('baby')) return;
  if (preferChild && tryDecrement('child')) return;

  // Fallback to any available
  if (tryDecrement('child')) return;
  tryDecrement('baby');
};

const getAdultPrice = (tour: TourLike, adultsInRoom: number) => {
  if (!tour) return 0;
  if (adultsInRoom <= 1) return toNumber(tour.turs_single);
  if (adultsInRoom === 2) return toNumber(tour.turs_double);
  return toNumber(tour.turs_triple);
};

const childPriceOptions = (tour: TourLike, age: number | string) => {
  const parsedAge = parseInt(String(age), 10);
  if (Number.isNaN(parsedAge)) return [];
  return (tour?.prices || []).filter(price =>
    ['turBaby', 'turChild', 'turChildNoBed'].includes(String(price?.turf_PaxType || '')) &&
    parsedAge >= toNumber(price?.turf_MinAge) &&
    parsedAge <= toNumber(price?.turf_MaxAge)
  );
};

const selectedChildPrice = (tour: TourLike, selection: NonNullable<ReservationSelection['childPriceSelections']>[number]) => {
  const options = childPriceOptions(tour, selection?.age ?? '');
  if (options.length === 1) return options[0];
  return options.find(price =>
    price.turf_PaxType === selection?.selectedPriceType &&
    (price.turf_RoomType || price.turf_PaxType) === selection?.selectedPriceId
  );
};

const getReservationTotals = (reservation: ReservationSelection) => {
  const rooms = reservation?.rooms || [];
  const adults = rooms.reduce((sum, room) => sum + toNumber(room.adult), 0);
  const roomKids = rooms.reduce(
    (sum, room) => sum + toNumber(room.child) + toNumber(room.baby),
    0
  );
  const childrenAges = Array.isArray(reservation.childrenAges)
    ? reservation.childrenAges
    : [];
  const ageCounts = getChildrenAgeCounts(childrenAges);

  const baby = childrenAges.length > 0 ? ageCounts.baby : rooms.reduce((sum, room) => sum + toNumber(room.baby), 0);
  const child = childrenAges.length > 0 ? ageCounts.child : rooms.reduce((sum, room) => sum + toNumber(room.child), 0);
  const unknown = childrenAges.length > 0 ? ageCounts.unknown : 0;

  return {
    adults: adults + ageCounts.adultExtra,
    rooms: rooms.length,
    baby,
    child,
    unknown,
    roomKids,
  };
};

const getTourTotal = (tour: TourLike, reservation: ReservationSelection) => {
  if (!tour || !reservation) return 0;
  const totals = getReservationTotals(reservation);
  if (totals.unknown > 0) return 0;

  const adultTotal = (reservation.rooms || []).reduce((sum, room) => {
    const adultCount = toNumber(room.adult);
    if (adultCount <= 0) return sum;
    return sum + adultCount * getAdultPrice(tour, adultCount);
  }, 0);

  const hasDetailedChildPrices = Array.isArray(tour.prices) && tour.prices.length > 0;
  const childTotal = hasDetailedChildPrices
    ? (reservation.childrenAges || []).reduce((sum, age, index) => {
      const price = selectedChildPrice(tour, {
        age,
        ...(reservation.childPriceSelections?.[index] || {})
      });
      return sum + toNumber(price?.turf_Price);
    }, 0)
    : totals.child * toNumber(tour.turs_child);
  const babyTotal = hasDetailedChildPrices ? 0 : totals.baby * toNumber(tour.turs_baby);

  return adultTotal + childTotal + babyTotal;
};

const loadReservationSelection = (reservation: ReservationSelection) => {
  if (typeof window === 'undefined') return;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (parsed?.rooms && Array.isArray(parsed.rooms)) {
      reservation.rooms = parsed.rooms.map((room: ReservationRoom) => ({
        adult: toNumber(room.adult) || 1,
        child: toNumber(room.child),
        baby: toNumber(room.baby),
        room_type: room.room_type || null,
        options: room.options || {},
      }));
    }
    if (Array.isArray(parsed?.childrenAges)) {
      reservation.childrenAges = parsed.childrenAges;
    }
    if (Array.isArray(parsed?.childPriceSelections)) {
      reservation.childPriceSelections = parsed.childPriceSelections;
    }
    normalizeChildrenAges(reservation);
  } catch (err) {
    // ignore
  }
};

const saveReservationSelection = (reservation: ReservationSelection) => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        rooms: reservation.rooms,
        childrenAges: reservation.childrenAges || [],
        childPriceSelections: reservation.childPriceSelections || [],
      })
    );
  } catch (err) {
    // ignore
  }
};

export const useReservationPricing = () => {
  return {
    getRoomKidsCount,
    getChildrenAgeCounts,
    normalizeChildrenAges,
    removeChildFromRooms,
    getReservationTotals,
    getTourTotal,
    loadReservationSelection,
    saveReservationSelection,
  };
};
